import { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import axios from 'axios';

import axiosInstance from 'apis/axiosInstance';
import CommonButton from 'components/CommonButton/CommonButton';
import DefaultModal from 'components/Modals/DefaultModal';
import TextField from 'components/TextField/TextField';
import { renderToast } from 'components/Toast/Toast';
import useAssignableApiKeyClinics from 'hooks/useAssignableApiKeyClinics';
import useModal from 'hooks/useModal';
import {
  ICreateClinicApiKeyForm,
  ICreatedClinicApiKey,
} from 'interfaces/clinicApiKeys';
import { GENERIC_ERROR_MESSAGE } from 'utils/constants';
import ApiKeyClinicDropdown from '../ApiKeyClinicDropdown/ApiKeyClinicDropdown';
import { loadSelectedClinicId } from 'utils/storage';

interface CreateApiKeyModalProps {
  onClose: () => void;
  onCreated: () => Promise<unknown>;
}

const getErrorMessage = (error: unknown) => {
  if (
    axios.isAxiosError(error) &&
    [400, 403].includes(error.response?.status ?? 0)
  ) {
    const message = error.response?.data?.message;
    return Array.isArray(message) ? message.join(', ') : message;
  }
  return GENERIC_ERROR_MESSAGE;
};

const CreateApiKeyModal: FC<CreateApiKeyModalProps> = ({
  onClose,
  onCreated,
}) => {
  const { setIsAllowed } = useModal({ onClose });

  const currentClinicId = loadSelectedClinicId();
  const { data: assignableClinics = [] } = useAssignableApiKeyClinics();

  // Only kept in memory while the modal is open; it is never shown again
  const [createdKey, setCreatedKey] = useState<ICreatedClinicApiKey | null>(
    null
  );

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = useForm<ICreateClinicApiKeyForm>({
    mode: 'all',
    defaultValues: { name: '', clinicIds: [] },
  });

  const onSubmit = async ({ name, clinicIds }: ICreateClinicApiKeyForm) => {
    setIsAllowed(false);
    try {
      const response = await axiosInstance.post<ICreatedClinicApiKey>(
        '/clinic-api-keys',
        {
          name: name.trim(),
          // The current clinic is always assigned by the backend
          ...(clinicIds.length > 0 && { clinicIds }),
        }
      );
      setCreatedKey(response.data);
      await onCreated();
    } catch (error) {
      setIsAllowed(true);
      renderToast({
        message: getErrorMessage(error) || GENERIC_ERROR_MESSAGE,
        type: 'error',
      });
    }
  };

  const handleCopy = async () => {
    if (!createdKey) return;
    await navigator.clipboard.writeText(createdKey.key);
    renderToast({ message: 'Copied successfully', type: 'success' });
  };

  return (
    <DefaultModal className="rounded-[1.6rem]">
      <div className="w-50 text-darkest-grey text-14 leading-[2.1rem]">
        <div className="px-3 pt-1.6 pb-1.3 border-b border-light-grey">
          <h3 className="text-20 leading-[3rem] font-bold">
            {createdKey ? 'API Key Created' : 'Create API Key'}
          </h3>
          <p>
            {createdKey
              ? 'Copy this key now. For security, it will not be shown again.'
              : 'Give the key a name so you can recognise where it is used'}
          </p>
        </div>
        {createdKey ? (
          <div className="px-3 pb-3.2 py-3 flex flex-col gap-y-2.4">
            <div className="flex items-center gap-x-1.6">
              <code
                data-testid="created-api-key"
                className="flex-1 break-all py-1.3 px-1.6 rounded-[1rem] border-[0.5px] border-lightest-grey shadow-primary"
              >
                {createdKey.key}
              </code>
              <CommonButton variant="secondary" onClick={handleCopy}>
                Copy
              </CommonButton>
            </div>
            <div className="flex justify-end">
              <CommonButton onClick={onClose}>Done</CommonButton>
            </div>
          </div>
        ) : (
          <form className="px-3 pb-3.2 py-3 flex flex-col gap-y-2.4">
            <TextField
              id="name"
              control={control}
              label="Key Name"
              placeholder="E.g. Marketing dashboard"
              rules={{
                validate: (value: string) => {
                  if (!value?.trim()) return 'This field is required';
                  if (value.trim().length > 64)
                    return 'Name must be at most 64 characters';
                },
              }}
            />
            <Controller
              name="clinicIds"
              control={control}
              render={({ field: { value, onChange } }) => (
                <ApiKeyClinicDropdown
                  clinics={assignableClinics}
                  currentClinicId={currentClinicId}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
            <div className="flex justify-end gap-x-1.6">
              <CommonButton
                variant="secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </CommonButton>
              <CommonButton
                disabled={!isValid}
                type="submit"
                isLoading={isSubmitting}
                onClick={handleSubmit(onSubmit)}
              >
                Create
              </CommonButton>
            </div>
          </form>
        )}
      </div>
    </DefaultModal>
  );
};

export default CreateApiKeyModal;
